import type { Doc } from 'yjs';
import { useCanvasStore, storeWhenSynced, storeYdoc } from '../store/canvas-store';
import { decodeDoc } from '../io/serialize';
import { syncToY } from '../store/ydoc/diff';
import { LOCAL_ORIGIN, ydocMaps } from '../store/ydoc/schema';
import { syncUrl } from './liveSync';
import { useCurrentProject } from './currentProject';
import { currentAccessToken } from '../auth/useAuth';
import { projectsApi } from './useProjects';
import { emptyState } from '../store/commands';

/** Seed an EMPTY synced room from a project's legacy JSON. No-op if the room is
 *  already populated. Runs inside a LOCAL_ORIGIN txn so the seed is one undo step
 *  and the provider pushes it upstream. */
export function bootstrapRoom(ydoc: Doc, legacyJson: string): void {
  const m = ydocMaps(ydoc);
  // Any synced data present → never clobber (a valid legacy doc can have zero
  // frames but non-empty themes/tours/comments/scalars).
  if (m.frames.size > 0 || m.themes.size > 0 || m.tours.size > 0 || m.comments.size > 0 || m.scalars.size > 0) return;
  const decoded = decodeDoc(legacyJson);
  ydoc.transact(() => syncToY(ydoc, emptyState().doc, decoded), LOCAL_ORIGIN);
}

/** Open a project in live-sync mode: fetch metadata + legacy doc, bind in sync
 *  mode, then bootstrap the room from legacy JSON once sync settles. */
export async function openProjectSynced(projectId: string): Promise<void> {
  const detail = await projectsApi.get(projectId);
  const token = currentAccessToken() ?? '';
  useCanvasStore.getState().load(
    { ...emptyState() },
    { sync: { wsUrl: syncUrl(projectId), room: projectId, token } },
  );
  useCurrentProject.getState().setCurrent(projectId, detail.docVersion, detail.title);
  useCurrentProject.getState().setRole(detail.role);

  await storeWhenSynced();
  const ydoc = storeYdoc();
  if (ydoc) bootstrapRoom(ydoc, detail.doc);
}
